/**
 * The accessibility vocabulary, shared by the browser and the server.
 *
 * A worksheet, quiz or marking sheet can be shaped for the pupil reading it.
 * Two things are being chosen, and they are independent of one another:
 *
 *   - how the text is set   (`a11y`) – typeface, size, spacing
 *   - what colour the paper is (`tint`) – the Irlen-style overlay a lot of
 *     pupils are already given a plastic sheet for
 *
 * They are separate because the combinations are real: "large print on cream"
 * is a normal thing for a SENCo to ask for, and folding the two into a single
 * dropdown turns five layouts and six tints into thirty options nobody can
 * scan.
 *
 * The list lives here rather than in app.js because the server has to agree
 * with it. The export endpoints are a public API, and a value that reaches the
 * stylesheet unchecked is an attribute-injection hole – so every entry point
 * runs the string through validateA11y/validateTint and takes null for
 * anything it does not recognise.
 *
 * Loaded as a plain script in the browser and required in Node, the same way
 * subjects.js and markSchemeFilter.js are.
 */
(function (root, factory) {
    if (typeof module === 'object' && module.exports) module.exports = factory();
    else root.A11yProfiles = factory();
}(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    // `id: null` is the default – no data-a11y attribute is written at all, so
    // the standard layout costs nothing and stays byte-identical to what the
    // product printed before any of this existed.
    //
    // `blurb` is the SENCo-facing explanation. It is shown in the settings
    // pane and on /accessibility, so it says who the layout is *for* rather
    // than which CSS it sets.
    const TEXT_LAYOUTS = [
        {
            id: null,
            label: 'Standard',
            blurb: 'Serif body text at the usual size.',
        },
        {
            id: 'dyslexia',
            label: 'Dyslexia-friendly',
            blurb: 'OpenDyslexic, with wider letter spacing and roomier lines.',
        },
        {
            id: 'large-print',
            label: 'Large print',
            blurb: 'Everything scaled up by a third, for low vision.',
        },
        {
            id: 'high-legibility',
            label: 'High-legibility sans',
            blurb: 'A plain sans-serif with open letterforms and wider spacing. '
                 + 'Worth trying for readers who find OpenDyslexic distracting.',
        },
        {
            id: 'low-density',
            label: 'Low density',
            blurb: 'Fewer things on the page: larger text, one question per block '
                 + 'and taller answer lines. For pupils who lose their place on a '
                 + 'crowded sheet.',
        },
    ];

    // Paper colours. These are deliberately pale – they sit behind body text
    // that has to stay legible, and they are printed, so a saturated colour
    // costs a fortune in ink and reads worse than white.
    //
    // Contrast against the #1a1d26 body colour is above 12:1 for all of them.
    const TINTS = [
        { id: null,     label: 'White',  hex: '#ffffff' },
        { id: 'beige',  label: 'Beige',  hex: '#ede8df' },
        { id: 'cream',  label: 'Cream',  hex: '#fdf6e3' },
        { id: 'blue',   label: 'Blue',   hex: '#eaf2fb' },
        { id: 'green',  label: 'Green',  hex: '#ecf6ec' },
        { id: 'pink',   label: 'Pink',   hex: '#fbeef2' },
        { id: 'yellow', label: 'Yellow', hex: '#fdf8e0' },
    ];

    const TEXT_LAYOUT_IDS = TEXT_LAYOUTS.map(l => l.id).filter(Boolean);
    const TINT_IDS = TINTS.map(t => t.id).filter(Boolean);

    /**
     * The layout id to render with, or null for standard.
     *
     * Null rather than a thrown error, because this runs on a query parameter
     * on a download link. A stale bookmark carrying a layout we have since
     * renamed should hand back a plain worksheet, not a 400 in the middle of a
     * lesson.
     */
    function validateA11y(value) {
        return TEXT_LAYOUT_IDS.includes(value) ? value : null;
    }

    /** The tint id to render with, or null for white. Same reasoning. */
    function validateTint(value) {
        return TINT_IDS.includes(value) ? value : null;
    }

    /** Both at once, in the shape the render options object already takes. */
    function validateRenderOpts(query) {
        query = query || {};
        return {
            a11y: validateA11y(query.a11y),
            tint: validateTint(query.tint),
        };
    }

    /**
     * The `?a11y=…&tint=…` suffix for an export URL, or '' when both are
     * standard. Keeps the two places that build download links from
     * disagreeing about the parameter names.
     */
    function toQuery(opts) {
        opts = opts || {};
        const parts = [];
        const a11y = validateA11y(opts.a11y);
        const tint = validateTint(opts.tint);
        if (a11y) parts.push('a11y=' + encodeURIComponent(a11y));
        if (tint) parts.push('tint=' + encodeURIComponent(tint));
        return parts.join('&');
    }

    function labelFor(list, id) {
        const hit = list.find(item => item.id === (id || null));
        return hit ? hit.label : list[0].label;
    }

    /** Human-readable summary, e.g. "Large print on cream". For the UI only. */
    function describe(opts) {
        opts = opts || {};
        const a11y = validateA11y(opts.a11y);
        const tint = validateTint(opts.tint);
        if (!a11y && !tint) return 'Standard';
        const layout = a11y ? labelFor(TEXT_LAYOUTS, a11y) : 'Standard';
        return tint ? layout + ' on ' + labelFor(TINTS, tint).toLowerCase() : layout;
    }

    return {
        TEXT_LAYOUTS,
        TINTS,
        TEXT_LAYOUT_IDS,
        TINT_IDS,
        validateA11y,
        validateTint,
        validateRenderOpts,
        toQuery,
        describe,
    };
}));
